import { useDataProvider } from 'context/DataProviderContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listConferenceRooms, getOfficeLocations } from 'services/Settings';

// Lists conference rooms using dedicated conference room API
// Returns flat options: { label, value, capacity, office_name, office_location_id, features }
export const useConferenceRooms = () => {
  const { conferenceRoomOptions, setConferenceRoomOptions } = useDataProvider();
  const [isConferenceRoomsLoading, setIsConferenceRoomsLoading] = useState(false);
  const hasAttemptedFetchRef = useRef(false);

  const loadConferenceRooms = useCallback(() => {
    setIsConferenceRoomsLoading(true);

    // Fetch both conference rooms and office locations to get office names
    Promise.all([listConferenceRooms(), getOfficeLocations()])
      .then(([roomsResponse, locationsResponse]) => {
        const rooms = roomsResponse?.data || [];
        const locations = locationsResponse?.data || [];

        // Create a map of office_location_id to office_name for quick lookup
        const officeNameMap = {};
        locations.forEach((loc) => {
          const locationId = loc?.office_location_id || loc?.sk;
          officeNameMap[locationId] = loc?.office_name;
        });

        const options = rooms.map((room) => ({
          label: room?.room_name || room?.name,
          value: room?.conference_room_id || room?.room_id || room?.id,
          capacity: room?.capacity,
          office_name: officeNameMap[room?.office_location_id] || 'Unknown Office',
          office_location_id: room?.office_location_id,
          features: room?.features,
        }));

        // Avoid triggering re-renders/loops when there are no rooms
        setConferenceRoomOptions((prev) => {
          if ((options?.length || 0) === 0) {
            return prev || [];
          }
          return options;
        });
      })
      .catch((error) => {
        console.error('Error loading conference rooms:', error);
      })
      .finally(() => setIsConferenceRoomsLoading(false));
  }, [setConferenceRoomOptions]);

  useEffect(() => {
    // Fetch only once per mount, even if the result is empty
    if (!hasAttemptedFetchRef.current && (!conferenceRoomOptions || conferenceRoomOptions.length === 0)) {
      hasAttemptedFetchRef.current = true;
      loadConferenceRooms();
    }
  }, [conferenceRoomOptions, loadConferenceRooms]);

  // Grouped by office for UI if needed
  const groupedByOffice = useMemo(() => {
    return (conferenceRoomOptions || []).reduce((acc, room) => {
      const key = room.office_name || 'Other';
      if (!acc[key]) acc[key] = [];
      acc[key].push(room);
      return acc;
    }, {});
  }, [conferenceRoomOptions]);

  // Build Dropdown-ready grouped options with capacity in label
  const groupedConferenceRoomOptions = useMemo(() => {
    const officeToRooms = {};
    (conferenceRoomOptions || []).forEach((room) => {
      const groupLabel = room?.office_name || 'Other';
      if (!officeToRooms[groupLabel]) officeToRooms[groupLabel] = [];
      const labelWithCapacity = room?.capacity ? `${room?.label} (${room?.capacity})` : room?.label;
      officeToRooms[groupLabel].push({
        label: labelWithCapacity,
        value: room?.value,
        capacity: room?.capacity,
        office_name: room?.office_name,
        office_location_id: room?.office_location_id,
      });
    });

    return Object.keys(officeToRooms).map((label) => ({
      label,
      items: officeToRooms[label].sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }, [conferenceRoomOptions]);

  return { conferenceRoomOptions, groupedByOffice, groupedConferenceRoomOptions, loadConferenceRooms, isConferenceRoomsLoading };
};
